import styled from "@emotion/styled";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

import Text from "@components/Text";
import { useLang } from "@src/hooks/useLang";
import {
  RangeInput,
  RangeWrap,
  Ramp,
  Shade,
  THUMB,
} from "@screens/TracksScreen/TrackFilterControls";
import {
  TIME_RAMP_GRADIENT,
  TIME_STEP_S,
  axisToTime,
  fromTimeInput,
  timeRampColor,
  timeToAxis,
  timeZoneLabel,
  timelineBreaks,
  timelineTicks,
  toTimeInput,
  type TTrackTimeline,
} from "@screens/FieldsScreen/fieldTrackTime";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  padding: 2px 6px;
  border: 1px solid #e0e2e7;
  border-radius: 4px;
  background: #fff;
  color: #494c5b;
  cursor: pointer;
  white-space: nowrap;
`;

/** When the machines were working: one bar per slice of the axis. */
const Histogram = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1px;
  height: 18px;
  margin: 8px ${THUMB / 2}px 0 ${THUMB / 2}px;
`;

const Bar = styled.div`
  flex: 1;
  min-height: 1px;
  border-radius: 1px 1px 0 0;
`;

/** Where a collapsed idle gap sits on the ramp. */
const Break = styled.div`
  position: absolute;
  top: -1px;
  bottom: -1px;
  width: 3px;
  margin-left: -1.5px;
  background: #fff;
`;

const Ticks = styled.div`
  position: relative;
  height: 14px;
  margin: 2px ${THUMB / 2}px 0 ${THUMB / 2}px;
`;

const Tick = styled.span`
  position: absolute;
  top: 0;
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  color: #8c8f9b;
  white-space: nowrap;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const TimeInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid #e0e2e7;
  border-radius: 4px;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  padding: 3px 4px;
  &:focus {
    outline: none;
    border-color: #68ad18;
  }
`;

/** Same pause-then-apply as the color filter: a day's swaths are rebuilt on
 *  each change, which is too heavy for every pointer move. */
const APPLY_DELAY_MS = 150;

type Props = {
  day: string;
  /** Null while the day's points are still loading. */
  timeline: TTrackTimeline | null;
  from: number | null;
  to: number | null;
  onWindow: (from: number, to: number) => void;
  onClose: () => void;
  /** Labels the axis can hold without colliding — wider panes take more. */
  maxTicks?: number;
};

/**
 * One worked day on a time axis: an activity histogram, a start/stop slider
 * over the time colour ramp with round local-time ticks, and exact start/stop
 * fields. Everything snaps to 10 s; idle stretches are collapsed so the slider
 * spends its width on time actually worked. Used by both map panes.
 */
const FieldTrackTimeline: React.FC<Props> = ({
  day,
  timeline: tl,
  from,
  to,
  onWindow,
  onClose,
  maxTicks = 4,
}) => {
  const lang = useLang();
  const t = lang.tracksTime;

  // Times (unix s) the handles show; the map follows once input pauses.
  const [range, setRange] = useState<[number, number] | null>(
    from != null && to != null ? [from, to] : null
  );
  const pending = useRef<[number, number] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draftFrom, setDraftFrom] = useState<string | null>(null);
  const [draftTo, setDraftTo] = useState<string | null>(null);

  useEffect(() => {
    if (pending.current) return;
    setRange(from != null && to != null ? [from, to] : null);
  }, [from, to]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  // The day list sits at the bottom of a scrolling panel; the slider opens
  // above it, out of sight, unless it is brought into view.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [day]);

  const flush = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const next = pending.current;
    pending.current = null;
    if (next) onWindow(next[0], next[1]);
  };

  const move = (next: [number, number]) => {
    setRange(next);
    pending.current = next;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, APPLY_DELAY_MS);
  };

  const header = (
    <Header>
      <Text size={12} weight={500}>
        {`${t.title} · ${moment(day).format("D MMM")}`}
      </Text>
      <CloseButton type="button" onClick={onClose}>
        {t.allDays}
      </CloseButton>
    </Header>
  );

  if (!tl || !range) {
    return (
      <div ref={rootRef}>
        {header}
        <Text size={11} color="#8c8f9b">
          {t.loading}
        </Text>
      </div>
    );
  }

  const [lo, hi] = range;
  const axisLo = timeToAxis(tl, lo);
  const axisHi = timeToAxis(tl, hi);
  const pct = (axis: number) => (axis / tl.axisLength) * 100;
  // Both handles parked at the right end: lift the lower one or it can never
  // be dragged out from under the upper.
  const loOnTop = axisLo === axisHi && axisLo > tl.axisLength / 2;
  const maxBin = Math.max(1, ...tl.bins);

  const editTime = (which: "from" | "to", value: string) => {
    (which === "from" ? setDraftFrom : setDraftTo)(value);
    const time = fromTimeInput(tl, value, which === "from" ? lo : hi);
    if (time == null) return;
    move(which === "from" ? [Math.min(time, hi), hi] : [lo, Math.max(time, lo)]);
  };

  const finishTime = () => {
    setDraftFrom(null);
    setDraftTo(null);
    flush();
  };

  return (
    <div ref={rootRef}>
      {header}

      <Histogram aria-hidden>
        {tl.bins.map((count, i) => {
          const mid = ((i + 0.5) / tl.bins.length) * tl.axisLength;
          const inside = mid >= axisLo && mid <= axisHi;
          return (
            <Bar
              key={i}
              style={{
                height: `${(count / maxBin) * 100}%`,
                background: inside ? timeRampColor(mid / tl.axisLength) : "#d5d8dd",
              }}
            />
          );
        })}
      </Histogram>

      <RangeWrap
        style={{ marginTop: 2 }}
        onPointerUp={flush}
        onKeyUp={flush}
        onTouchEnd={flush}
      >
        <Ramp style={{ background: TIME_RAMP_GRADIENT }}>
          <Shade style={{ left: 0, width: `${pct(axisLo)}%` }} />
          <Shade style={{ left: `${pct(axisHi)}%`, right: 0 }} />
          {timelineBreaks(tl).map((f) => (
            <Break key={f} style={{ left: `${f * 100}%` }} />
          ))}
        </Ramp>
        <RangeInput
          type="range"
          min={0}
          max={tl.axisLength}
          step={TIME_STEP_S}
          value={axisLo}
          style={loOnTop ? { zIndex: 2 } : undefined}
          aria-label={t.from}
          onChange={(e) => move([Math.min(axisToTime(tl, Number(e.target.value)), hi), hi])}
        />
        <RangeInput
          type="range"
          min={0}
          max={tl.axisLength}
          step={TIME_STEP_S}
          value={axisHi}
          aria-label={t.to}
          onChange={(e) => move([lo, Math.max(axisToTime(tl, Number(e.target.value)), lo)])}
        />
      </RangeWrap>

      <Ticks aria-hidden>
        {timelineTicks(tl, maxTicks).map(({ fraction, label }) => (
          <Tick
            key={fraction}
            style={{
              left: `${fraction * 100}%`,
              // Keep the end labels inside the box.
              transform:
                fraction < 0.08 ? "none" : fraction > 0.92 ? "translateX(-100%)" : "translateX(-50%)",
            }}
          >
            {label}
          </Tick>
        ))}
      </Ticks>

      <TimeRow>
        <TimeInput
          type="time"
          step={TIME_STEP_S}
          aria-label={t.from}
          value={draftFrom ?? toTimeInput(lo)}
          onChange={(e) => editTime("from", e.target.value)}
          onBlur={finishTime}
          onKeyDown={(e) => e.key === "Enter" && finishTime()}
        />
        <Text size={12} color="#8c8f9b" fitContent>
          –
        </Text>
        <TimeInput
          type="time"
          step={TIME_STEP_S}
          aria-label={t.to}
          value={draftTo ?? toTimeInput(hi)}
          onChange={(e) => editTime("to", e.target.value)}
          onBlur={finishTime}
          onKeyDown={(e) => e.key === "Enter" && finishTime()}
        />
      </TimeRow>
      <Text size={11} color="#8c8f9b" style={{ marginTop: 4 }}>
        {t.zone.replace("{tz}", timeZoneLabel(tl.start))}
      </Text>
    </div>
  );
};

export default FieldTrackTimeline;
